import { type NodeCommunity, type Pagination, createNodeCommunity } from 'models';
import { type UseQueryOptions, useQuery } from 'query';
import type { PaginationData } from 'common/services';
import type { ApiClientResponseError, ResponseErrorDataObject } from 'common/services/api';
import { findNodeCommunities } from '../services';
import { getNodeCommunitiesListQueryKey } from './keys';

export type NodeCommunitiesQueryFilter = {
  admin: string;
  ids?: string[];
  search?: string;
};

export const useGetNodeCommunities = ({
  filter,
  options,
  pagination,
}: {
  filter: NodeCommunitiesQueryFilter;
  options?: Omit<
    UseQueryOptions<
      PaginationData<NodeCommunity>,
      ApiClientResponseError<ResponseErrorDataObject>,
      PaginationData<NodeCommunity>
    >,
    'queryFn' | 'queryKey'
  >;
  pagination?: Partial<Pagination>;
}) =>
  useQuery<
    PaginationData<NodeCommunity>,
    ApiClientResponseError<ResponseErrorDataObject>,
    PaginationData<NodeCommunity>
  >({
    async queryFn() {
      const pageSize = pagination?.pageSize ?? 10;
      const response = await findNodeCommunities({
        config: {
          params: {
            _id: filter.ids ? { $in: filter.ids } : undefined,
            $limit: pageSize,
            $skip: ((pagination?.position ?? 1) - 1) * pageSize,
            admin: filter.admin,
            name: filter.search ? { $startsWith: filter.search } : undefined,
          },
        },
      });

      return {
        data: response.data.data.map((item) => createNodeCommunity(item)),
        limit: response.data.limit,
        skip: response.data.skip,
        total: response.data.total,
      };
    },
    queryKey: getNodeCommunitiesListQueryKey({ filter }),
    ...options,
  });
